
import {
  Box,
  Stack,
  Typography,
  IconButton,
  ChipProps,
  Chip,
  Avatar,
  useTheme
} from '@mui/material';

import { CustomIcon } from 'modules/components/layer00';
import { StyledDisplayField } from 'modules/components/layer01';
import {
  RemoveIncidentModal,
  RemoveInspectionModal,
  RemoveMaterialDeliveryModal,
  RemoveOnSiteWorkEventModal,
  SaveAccidentModal,
  SaveDeliveryModal,
  SaveInspectionModal,
  SaveOnSiteWorkEventModal
} from 'modules/components/layer17';
import React, {
  ReactElement,
  ReactNode,
  useMemo,
  useState
} from 'react';
import { theme } from 'theme';


export type IReportItemType =
  'INSPECTION' |
  'ACCIDENT' |
  'DELIVERY' |
  'CONTRACTOR';

type IProps = {
  id: string;
  isVisible: boolean;
  handleClose: () => void;
};

type IBaseModalProps = (props: IProps) => JSX.Element;

const __reportItemType2Modal: Record<IReportItemType, IBaseModalProps
> = {
  INSPECTION: (props: IProps) => <SaveInspectionModal.FC {...props} />,
  ACCIDENT: (props: IProps) => <SaveAccidentModal.FC {...props} />,
  DELIVERY: (props: IProps) => <SaveDeliveryModal.FC {...props} />,
  CONTRACTOR: (props: IProps) => <SaveOnSiteWorkEventModal.FC {...props} />
};
const __reportItemType2RemoveModal: Record<IReportItemType, IBaseModalProps
> = {
  INSPECTION: (props: IProps) => <RemoveInspectionModal.FC {...props} />,
  ACCIDENT: (props: IProps) => <RemoveIncidentModal.FC {...props} />,
  DELIVERY: (props: IProps) => <RemoveMaterialDeliveryModal.FC {...props} />,
  CONTRACTOR: (props: IProps) => <RemoveOnSiteWorkEventModal.FC {...props} />
};

const __reportItemType2BaseTitle: Record<IReportItemType, string> = {
  INSPECTION: 'System Inspected',
  ACCIDENT: 'Involved',
  CONTRACTOR: '',
  DELIVERY: ''
};



type IDeliveryDetails = {
  type: 'DELIVERY';
  quantity: number;
  name: string;
  damages: string;
  issues: string;
  events: string;
  description: string;
  finishName: string;
  finishId: string;
  deliveryImgUrls: string[];
};

type IInspectionDetails = {
  type: 'INSPECTION';
  systemName: string;
  description: string;
  inspectionResultName: ChipProps['variant'];
  inspectionResultDisplayName: string;
};

type IIncidentDetails = {
  type: 'ACCIDENT';
  involved: {
    name: string;
    imgUrl: string;
  }[];
  description: string;
};

type IWorkEventDetails = {
  type: 'CONTRACTOR';
  personName: string;
  profileImg: string;
  hours: number;
  description: string;
};

type ItemDetail = IDeliveryDetails | IWorkEventDetails | IIncidentDetails | IInspectionDetails;

export type DailyReportItemProps = {
  id: string;
  details: ItemDetail;
  reportItemType: IReportItemType;
};

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = (props: DailyReportItemProps) => {
  const [
    isVisible,
    setIsVisible
  ] = useState<boolean>(false);
  const [
    isDeleteVisible,
    setIsDeleteVisible
  ] = useState<boolean>(false);

  const fnShowModal = __reportItemType2Modal[props.reportItemType];
  const fnDeleteModal = __reportItemType2RemoveModal[props.reportItemType];

  const CartTitle = useMemo(() => {
    return titleComponent[props.reportItemType];
  }, [props.reportItemType]);

  return (
    <>
      {isVisible && (
        fnShowModal({
          id: props.id,
          isVisible: isVisible,
          handleClose: () => setIsVisible((prev) => !prev)
        })
      )}
      {isDeleteVisible && (
        fnDeleteModal({
          id: props.id,
          isVisible: isDeleteVisible,
          handleClose: () => setIsDeleteVisible((prev) => !prev)
        })
      )}

      <Stack
        borderRadius='1rem'
        padding='1rem'
        rowGap='.75rem'
        bgcolor={theme.palette.secondary.main}
      >
        {/* TITLE */}
        <Stack
          direction='row'
          justifyContent='space-between'
        >
          {props.reportItemType !== 'CONTRACTOR'
            ? (
              <BaseTitleLayout
                type={props.reportItemType}
              >
                <CartTitle {...props.details} />
              </BaseTitleLayout>
            )
            : (
              <CartTitle {...props.details} />
            )}

          <Stack
            direction='row'
            columnGap='.5rem'
          >
            <IconButton
              onClick={() => {
                setIsDeleteVisible(() => true);
              }}
              sx={{
                borderRadius: '.625rem',
                bgcolor: theme.palette.error.main
              }}
            >
              <CustomIcon.large.FC
                icon='DELETE'
                stroke='white'
              />
            </IconButton>
            <IconButton
              onClick={() => setIsVisible(() => true)}
              sx={{
                borderRadius: '.625rem',
                bgcolor: theme.palette.primary.main
              }}
            >
              <CustomIcon.large.FC
                icon='EDIT'
                stroke='white'
              />
            </IconButton>
          </Stack>

        </Stack>

        <BaseDetailLayout
          detail={props.details}
        />


      </Stack>
    </>
  );
};


const titleComponent: Record<IReportItemType, (props: ItemDetail) => ReactElement> = {
  INSPECTION: (props: ItemDetail) => <InspectionTitle {...props} />,
  DELIVERY: (props: ItemDetail) => <DeliveryTitle {...props} />,
  ACCIDENT: (props: ItemDetail) => <IncidentTitle {...props} />,
  CONTRACTOR: (props: ItemDetail) => <WorkLogEventTitle {...props} />
};

const InspectionTitle = (props: ItemDetail) => {
  if (props.type !== 'INSPECTION') {
    return <></>;
  }
  return (
    <Stack
      direction='row'
      alignItems='center'
      columnGap='.5rem'
    >
      <Typography
        variant='bodyBold'
        color={theme.palette.primary.main}
      >
        {props.systemName}
      </Typography>
      <Chip
        variant={props.inspectionResultName}
        label={props.inspectionResultDisplayName}
      />
    </Stack>
  );
};


const WorkLogEventTitle = (props: ItemDetail) => {
  const theme = useTheme();
  if (props.type !== 'CONTRACTOR') {
    return <></>;
  }

  return (
    <Stack
      direction='row'
      alignItems='center'
      columnGap='.5rem'
      flex='1 1 0'
    >
      <Avatar
        src={props.profileImg ?? ''}
      />
      <Typography
        variant='bodyBold'
        color={theme.palette.primary.main}
      >
        {props.personName}
      </Typography>
      <Box
        flex='1 1 0'
        paddingRight='1rem'
        display='inline-flex'
        justifyContent='flex-end'
      >

        <CustomIcon.large.FC
          icon='CLOCK'
          stroke={theme.palette.primary.main}
        />
        <Typography
          marginLeft='.5rem'
          variant='bodyBold'
          color={theme.palette.primary.main}
        >
          {`${props.hours} Hours Logged`}
        </Typography>
      </Box>
    </Stack>
  );
};


const DeliveryTitle = (props: ItemDetail) => {
  if (props.type !== 'DELIVERY') {
    return <></>;
  }
  return (
    <Box
      display='inline-flex'
      columnGap='.5rem'
    >
      <Typography
        variant='body1'
        color={theme.palette.primary.main}
      >
        {`${props.quantity}x `}
      </Typography>
      <Typography
        variant='bodyBold'
        color={theme.palette.primary.main}
      >
        {props.name}
      </Typography>
    </Box>

  );
};


const IncidentTitle = (props: ItemDetail) => {
  if (props.type !== 'ACCIDENT') {
    return <></>;
  }
  return (
    <Box>
      <Typography
        variant='bodyBold'
        color={theme.palette.primary.main}
      >
        {props.involved.map((node) => node.name).join(', ')}
      </Typography>
    </Box>
  );
};


const BaseTitleLayout = (props: { children: ReactNode; type: IReportItemType }) => {
  return (
    <StyledDisplayField.FC
      icon={(
        <CustomIcon.large.FC
          icon='ACCOUNT'
          stroke={theme.palette.primary.main}
        />
      )}
      label={__reportItemType2BaseTitle[props.type]}
    >
      <>
        {props.children}
      </>
    </StyledDisplayField.FC>
  );
};


const BaseDetailLayout = (props: { detail: ItemDetail }) => {
  const theme = useTheme();
  return (
    <Stack
      rowGap='1rem'
    >
      <Typography
        variant='formLabel'
      >
        Description
      </Typography>
      <Typography
        variant='formInput'
      >
        {props.detail.description ?? ''}
      </Typography>
      {props.detail.type === 'DELIVERY' && (
        <Box
          display='flex'
          flexDirection='column'
          rowGap='.5rem'
        >
          <StyledDisplayField.FC
            icon={(
              <CustomIcon.large.FC
                icon='DRAGNDROP'
                stroke={theme.palette.primary.main}
              />
            )}
            label={__damageFields2Labels['damages']}
          >
            <Typography
              variant='formInput'
            >
              {props.detail.damages}
            </Typography>
          </StyledDisplayField.FC>
          <StyledDisplayField.FC
            icon={(
              <CustomIcon.large.FC
                icon='ALERT'
                stroke={theme.palette.primary.main}
              />
            )}
            label={__damageFields2Labels['issues']}
          >
            <Typography
              variant='formInput'
            >
              {props.detail.issues}
            </Typography>
          </StyledDisplayField.FC>
          <StyledDisplayField.FC
            icon={(
              <CustomIcon.large.FC
                icon='BELL'
                stroke={theme.palette.primary.main}
              />
            )}
            label={__damageFields2Labels['otherNotableEvents']}
          >
            <Typography
              variant='formInput'
            >
              {props.detail.events}
            </Typography>
          </StyledDisplayField.FC>
          <StyledDisplayField.FC
            icon={(
              <CustomIcon.large.FC
                icon='SPARKLE'
                stroke={theme.palette.primary.main}
              />
            )}
            label={__damageFields2Labels['finish']}
          >
            <Typography
              variant='formInput'
            >
              {props.detail.finishName}
            </Typography>
          </StyledDisplayField.FC>
          <Stack
            marginTop='1rem'
            direction='row'
          >
            {props.detail.deliveryImgUrls.map((rec, i) => (
              <Box
                borderRadius='.5rem'
                key={`img-${i}`}
                component='img'
                src={rec}
                height='4.5rem'
                width='4.5rem'
              />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

const damageFieldNames = [
  'damages',
  'issues',
  'otherNotableEvents',
  'finish'
] as const;

const __damageFields2Labels: Record<typeof damageFieldNames[number], string> = {
  damages: 'Damages',
  issues: 'Issues',
  otherNotableEvents: 'Other Notable Events',
  finish: 'Finish'
};
